import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// UI / Vistas
import Sidebar from "./components/WebParts/Sidebar";
import NotFound from "./components/WebParts/NotFound";
import Login from "./views/Login";
import ProtectedRoute from "./components/WebParts/ProtectedRoute";

import Dashboard from "./views/fut/Dashboard";
import AdminDeportistas from "./views/fut/Deportistas";
import AdminGrupos from "./views/fut/Grupos";
import AdminEventos from "./views/fut/Eventos";
import AdminAsistencias from "./views/fut/Asistencias";
import AdminRepresentantes from "./views/fut/Representantes";
import AdminColaboradores from "./views/fut/Colaboradores";
import Pagos from "./views/admin/Pagos";
import Productos from "./views/fut/Productos";

// Tema
import useAuth, { AuthProvider } from "./hooks/useAuth";

// Auth real (Supabase/localStorage)
import {
  readAuthSession,
  isTokenExpired,
  logout as hardLogout,
  getAuthHeader,
  getCurrentUser,
} from "./services/authService";
import AdminNotifications from "./views/admin/AdminNotifications";
import ResetPasswordView from "./views/ResetPassword";
import AdminRoles from "./views/admin/AdminRoles";

/* =========================================================
   Contexto ligero para exponer sesión y header con token
   ========================================================= */
export const AuthSessionContext = React.createContext({
  session: null,
  user: null,
  isAuthenticated: false,
  token: null,
  authHeader: () => ({}),
  refreshFromStorage: () => {},
  logout: () => {},
});

/* =========================================================
   Hook de sesión: lee localStorage y valida expiración
   ========================================================= */
function useAuthSessionState() {
  const read = useCallback(() => {
    const s = readAuthSession();
    const currentPath = window.location.pathname;

    if (!s) {
      if (currentPath.startsWith("/reset-password")) {
        return {
          session: null,
          user: null,
          token: null,
          isAuthenticated: false,
          allowGuest: true,
        };
      }
      return { session: null, user: null, token: null, isAuthenticated: false };
    }
    const expired = isTokenExpired(s.expires_at);
    if (expired) {
      hardLogout();
      if (currentPath.startsWith("/reset-password")) {
        return {
          session: null,
          user: null,
          token: null,
          isAuthenticated: false,
          allowGuest: true,
        };
      }
      return { session: null, user: null, token: null, isAuthenticated: false };
    }
    return {
      session: s,
      user: s.user || getCurrentUser(),
      token: s.access_token || null,
      isAuthenticated: true,
    };
  }, []);

  const [state, setState] = useState(() => read());

  const refreshFromStorage = useCallback(() => {
    setState(read());
  }, [read]);

  const logout = useCallback(() => {
    hardLogout();
    setState({
      session: null,
      user: null,
      token: null,
      isAuthenticated: false,
    });
  }, []);

  // Escucha cambios de storage (otro tab) y foco de ventana
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth") refreshFromStorage();
    };
    const onFocus = () => refreshFromStorage();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshFromStorage]);

  return {
    ...state,
    authHeader: getAuthHeader,
    refreshFromStorage,
    logout,
  };
}

/* =========================================================
   Guard de rutas protegidas (solo autenticación básica)
   ========================================================= */
function AuthGuard() {
  const { isAuthenticated } = React.useContext(AuthSessionContext);
  const location = useLocation();

  if (!isAuthenticated) {
    if (location.pathname.startsWith("/reset-password")) {
      return <Outlet />;
    }
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

/* =========================================================
   App interna: usa el guard + sidebar condicional
   ========================================================= */
function AppInner() {
  const location = useLocation();
  const { bgColor, lettersColor } = useAuth();
  const auth = React.useContext(AuthSessionContext);

  const isLoginPath =
    location.pathname === "/" || location.pathname === "/login";
  const showSidebar = auth.isAuthenticated && !isLoginPath;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const closeSidebar = () => setIsMenuOpen(false);

  const rootClass = useMemo(
    () => `w-full min-h-screen flex ${bgColor} ${lettersColor}`,
    [bgColor, lettersColor]
  );

  const sidebarWidth = isCollapsed ? 80 : 240;

  const mainStyle = useMemo(() => {
    if (!showSidebar || isMobile) return { marginLeft: 0 };
    return {
      marginLeft: sidebarWidth,
      transition: "margin-left 0.3s ease",
      width: `calc(100% - ${sidebarWidth}px)`,
    };
  }, [showSidebar, isMobile, sidebarWidth]);

  return (
    <div className={rootClass}>
      {showSidebar && (
        <Sidebar
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          // En desktop no usamos isMenuOpen, siempre lo dejamos "abierto"
          isOpen={isMobile ? isMenuOpen : true}
          onClose={closeSidebar}
          onToggleCollapse={() => setIsCollapsed((v) => !v)}
          // Para que el botón flotante pueda abrir el menú en móvil
          onOpenMobile={() => setIsMenuOpen(true)}
        />
      )}

      <main style={mainStyle} className="flex-1 transition-all duration-300">
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/reset-password" element={<ResetPasswordView />} />

          {/* Rutas protegidas con validación de permisos */}
          <Route element={<AuthGuard />}>
            <Route
              path="/futbolito"
              element={
                <ProtectedRoute requiredModule="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/dashboard"
              element={
                <ProtectedRoute requiredModule="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/deportistas"
              element={
                <ProtectedRoute requiredModule="deportistas">
                  <AdminDeportistas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/grupos"
              element={
                <ProtectedRoute requiredModule="grupos">
                  <AdminGrupos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/eventos"
              element={
                <ProtectedRoute requiredModule="eventos">
                  <AdminEventos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/asistencias"
              element={
                <ProtectedRoute requiredModule="asistencias">
                  <AdminAsistencias />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/representantes"
              element={
                <ProtectedRoute requiredModule="representantes">
                  <AdminRepresentantes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/colaboradores"
              element={
                <ProtectedRoute requiredModule="colaboradores">
                  <AdminColaboradores />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/notificaciones"
              element={
                <ProtectedRoute requiredModule="notificaciones">
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/roles"
              element={
                <ProtectedRoute requiredModule="roles">
                  <AdminRoles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/pagos"
              element={
                <ProtectedRoute requiredModule="pagos">
                  <Pagos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futbolito/productos"
              element={
                <ProtectedRoute requiredModule="productos">
                  <Productos />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </main>
    </div>
  );
}

/* =========================================================
   App wrapper: provee tema + sesión auth
   ========================================================= */
function AppWithProviders() {
  const sessionState = useAuthSessionState();

  const ctxValue = useMemo(
    () => ({
      session: sessionState.session,
      user: sessionState.user,
      isAuthenticated: sessionState.isAuthenticated,
      token: sessionState.token,
      authHeader: sessionState.authHeader,
      refreshFromStorage: sessionState.refreshFromStorage,
      logout: sessionState.logout,
    }),
    [sessionState]
  );

  return (
    <AuthSessionContext.Provider value={ctxValue}>
      <AppInner />
    </AuthSessionContext.Provider>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <AppWithProviders />
      </AuthProvider>
    </Router>
  );
}
